import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { GlobalState } from '@/store';
import { useLocation, useHistory } from 'react-router-dom';
import { Message } from '@arco-design/web-react';

// 引入redux store
import { createStore } from 'redux';
import rootReducer from '@/store';

// 创建store实例
const store = createStore(rootReducer);

// 设置全局axios配置
axios.defaults.timeout = 10000; // 10秒超时
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // 处理超时错误
    if (
      error.code === 'ECONNABORTED' &&
      error.message &&
      error.message.includes('timeout')
    ) {
      console.log('请求超时:', error.config?.url);
      // 避免重复提示
      if (error.config?.url !== '/api/auth/verify') {
        Message.error('请求超时，请稍后重试');
      }
    }

    // 处理403错误
    if (error.response?.status === 403) {
      console.log('权限不足(403):', error.config?.url);
      // 避免重复提示
      if (error.config?.url !== '/api/auth/verify') {
        Message.error('您没有权限访问该资源');
      }
    }

    return Promise.reject(error);
  }
);

// 公开页面，不需要登录即可访问
const publicRoutes = ['/login', '/terms-of-service', '/privacy-policy'];

/**
 * 检查用户是否登录（简易版）
 * @returns {boolean} 是否登录
 */
export function isLoggedIn() {
  return localStorage.getItem('userStatus') === 'login';
}

// 用于跟踪验证状态的变量，防止重复请求
let verificationInProgress = false;
let lastVerificationResult = null;
let lastVerificationTime = 0;
const VERIFICATION_CACHE_TIME = 10000; // 10秒缓存时间
const MAX_RETRY_ATTEMPTS = 2; // 最大重试次数
const RETRY_DELAY = 1000; // 重试延迟时间(毫秒)

/**
 * 验证token有效性
 * @param {number} retryCount 当前重试次数
 * @returns {Promise<boolean | {valid: boolean, user: any}>} 验证结果
 */
export const verifyToken = async (retryCount = 0) => {
  // 如果验证已在进行中，返回上次结果或等待
  const now = Date.now();
  if (verificationInProgress) {
    console.log('验证请求已在进行中，避免重复请求');
    // 使用缓存结果，如果缓存时间有效
    if (
      lastVerificationResult &&
      now - lastVerificationTime < VERIFICATION_CACHE_TIME
    ) {
      return lastVerificationResult;
    }

    // 等待一小段时间后返回新的检查结果，避免频繁发起请求
    await new Promise((resolve) => setTimeout(resolve, 100));
    return verifyToken(retryCount);
  }

  // 使用缓存结果，如果缓存时间有效
  if (
    lastVerificationResult &&
    now - lastVerificationTime < VERIFICATION_CACHE_TIME
  ) {
    console.log('使用缓存的验证结果');
    return lastVerificationResult;
  }

  // 标记验证开始
  verificationInProgress = true;

  try {
    // 尝试从本地存储获取token
    const token = localStorage.getItem('token');

    // 设置请求头选项
    const options = token
      ? {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000, // 5秒超时
        }
      : { timeout: 5000 };

    console.log('发送验证请求到 /api/auth/verify');
    const res = await axios.get('/api/auth/verify', options);

    if (res.data && res.data.valid && res.data.user) {
      // 更新用户信息
      const userInfo = res.data.user;

      // 如果从后端获取了权限信息，则单独更新权限
      if (userInfo.permissions) {
        store.dispatch({
          type: 'update-permissions',
          payload: { permissions: userInfo.permissions },
        });
      }

      store.dispatch({
        type: 'update-userInfo',
        payload: { userInfo },
      });

      // 缓存验证结果
      lastVerificationResult = { valid: true, user: userInfo };
      lastVerificationTime = Date.now();
      return lastVerificationResult;
    }

    // 验证失败，清除登录信息
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('userStatus');

    // 缓存验证结果
    lastVerificationResult = { valid: false, user: null };
    lastVerificationTime = Date.now();
    return lastVerificationResult;
  } catch (error) {
    console.error('Token验证失败:', error);

    // 如果是网络错误或403错误并且未超过最大重试次数，尝试重试
    if (
      (error.code === 'ECONNABORTED' || error.response?.status === 403) &&
      retryCount < MAX_RETRY_ATTEMPTS
    ) {
      console.log(
        `验证请求失败，${RETRY_DELAY}ms后进行第${retryCount + 1}次重试`
      );

      // 重置验证状态
      verificationInProgress = false;

      // 延迟后重试
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      return verifyToken(retryCount + 1);
    }

    // 清除无效的登录信息
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('userStatus');

    // 标记登录流程开始
    sessionStorage.setItem('login_in_progress', 'true');

    // 缓存验证结果
    lastVerificationResult = { valid: false, user: null };
    lastVerificationTime = Date.now();
    return lastVerificationResult;
  } finally {
    // 标记验证结束
    verificationInProgress = false;
  }
};

/**
 * 用户登出
 * @returns {Promise<boolean>} 登出是否成功
 */
export const logout = async () => {
  try {
    // 获取token（如果有）
    const token = localStorage.getItem('token');
    const options = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};

    // 调用登出接口
    await axios.post('/api/auth/logout', {}, options);
  } catch (error) {
    console.error('登出请求失败:', error);
  }

  // 无论接口成功与否，都清空本地用户信息
  localStorage.removeItem('token');
  localStorage.removeItem('userInfo');
  localStorage.removeItem('userStatus');

  store.dispatch({
    type: 'update-userInfo',
    payload: { userInfo: {} },
  });

  // 重定向到登录页
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }

  return true;
};

/**
 * 检查登录高阶组件 - 用于保护需要登录的组件
 * @param {React.ComponentType<any>} BaseComponent 需要保护的组件
 * @returns {React.ComponentType<any>} 包装后的组件
 */
export default function checkLogin(BaseComponent: React.ComponentType<any>) {
  return function AuthComponent(props: any) {
    const { userInfo, userLoading } = useSelector(
      (state: GlobalState) => state
    );
    const location = useLocation();
    const history = useHistory();

    useEffect(() => {
      // 如果是公开页面，不需要验证登录
      if (publicRoutes.includes(location.pathname)) {
        return;
      }

      // 只有未登录状态才需要验证
      if (!userInfo || !userInfo.id) {
        verifyToken().then((result) => {
          const isValid = typeof result === 'boolean' ? result : result.valid;

          if (!isValid) {
            sessionStorage.setItem('login_in_progress', 'true');

            // 修改重定向URL生成方式
            const redirectPath = encodeURIComponent(location.pathname);
            const redirectSearch = location.search
              ? encodeURIComponent(location.search)
              : '';
            const redirectUrl = `/login?redirect=${redirectPath}${redirectSearch}`;
            history.push(redirectUrl);
          } else {
            sessionStorage.removeItem('login_in_progress');
          }
        });
      }
    }, [location.pathname, userInfo, history]);

    // 修复渲染问题
    return <BaseComponent {...props} />;
  };
}
